import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { X } from "lucide-react";

const timePeriodOptions = [
    { value: "today", label: "Today" },
    { value: "weekly", label: "This Week" },
    { value: "monthly", label: "This Month" },
    { value: "tomorrow", label: "Tomorrow" },
    { value: "nextweek", label: "Next Week" },
    { value: "nextmonth", label: "Next Month" },
    { value: "custom", label: "Custom" },
];

export default function AddPlanModal({
    onClose,
    onSubmit,
    initialTimePeriod = "today",
    mode = "create-timeframe", // "create-timeframe" or "create-custom"
}) {
    const [planTitle, setPlanTitle] = React.useState("");
    const [timePeriod, setTimePeriod] = React.useState(initialTimePeriod);
    const [fromDate, setFromDate] = React.useState("");
    const [toDate, setToDate] = React.useState("");

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                onClose();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onClose]);

    const handleSubmit = () => {
        if (!planTitle.trim()) {
            alert("Please enter a plan title");
            return;
        }

        onSubmit({
            title: planTitle,
            timePeriod: timePeriod,
            fromDate: fromDate || null,
            toDate: toDate || null,
        });
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const modalTitle = mode === "create-custom" ? "Create Custom Period" : "Create Plan";
    const showTimePeriodSelection = mode === "create-timeframe";

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 px-4" onClick={handleBackdropClick}>
            <div className="bg-[#1E1F25] border border-[#2A2B32] rounded-2xl shadow-xl p-6 w-full max-w-md relative">
                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-200 transition"
                >
                    <X size={20} />
                </button>

                {/* Title */}
                <h2 className="text-xl font-semibold text-gray-100 mb-6 pr-8">
                    {modalTitle}
                </h2>

                {/* Inputs */}
                <div className="flex flex-col gap-5">
                    {/* Plan Title */}
                    <div className="flex flex-col gap-2">
                        <label htmlFor="plan-title" className="text-gray-300 text-sm font-medium">Plan Title</label>
                        <input
                            id="plan-title"
                            type="text"
                            value={planTitle}
                            onChange={(e) => setPlanTitle(e.target.value)}
                            className="bg-[#2A2B32] text-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder:text-gray-500"
                            placeholder="e.g., My plan"
                        />
                    </div>

                    {/* Time Period Selection - only shown in create-timeframe mode */}
                    {showTimePeriodSelection && (
                        <div className="flex flex-col gap-2">
                            <div className="text-gray-300 text-sm font-medium">Time Period</div>
                            <div className="grid grid-cols-2 gap-2">
                                {timePeriodOptions.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => setTimePeriod(option.value)}
                                        className={`px-3 py-2 rounded-lg text-xs font-medium transition ${
                                            timePeriod === option.value
                                                ? "bg-indigo-600 text-white"
                                                : "bg-[#2A2B32] text-gray-300 hover:bg-[#323844]"
                                        }`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Date Range Selection */}
                    <div className="flex flex-col gap-3 border-t border-[#2A2B32] pt-3">
                        <div className="text-gray-300 text-sm font-medium">Date Range (Optional)</div>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="flex flex-col gap-1">
                                <label htmlFor="from-date" className="text-gray-400 text-xs">From</label>
                                <input
                                    id="from-date"
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    className="bg-[#2A2B32] text-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <label htmlFor="to-date" className="text-gray-400 text-xs">To</label>
                                <input
                                    id="to-date"
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    className="bg-[#2A2B32] text-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-3 mt-6 border-t border-[#2A2B32] pt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-[#2A2B32] transition text-sm"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleSubmit}
                        className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-500 transition text-sm"
                    >
                        Create
                    </button>
                </div>
            </div>
        </div>
    );
}

AddPlanModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    initialTimePeriod: PropTypes.string,
    mode: PropTypes.oneOf(["create-timeframe", "create-custom"]),
};
